import { useEffect, useRef, useState } from 'react';

/**
 * Returns true once the element has scrolled into view at least `threshold`.
 * Sticky: stays true after first intersection so the section keeps its
 * faded-up state on scroll-back.
 */
export function useInView<T extends HTMLElement>(
  threshold = 0.2,
): [React.RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [seen, threshold]);

  return [ref as React.RefObject<T>, seen];
}
